/* eslint-disable jsx-a11y/media-has-caption -- streams de videochamada em tempo real não têm faixa de legendas */
import { useEffect } from 'react'
import { Mic, MicOff, PhoneOff, Video, VideoOff } from 'lucide-react'
import { useDailyRoom } from './useDailyRoom'

interface DailyVideoRoomProps {
  /** URL da sala Daily (dailyRoomUrl da sessão). */
  url: string
  /** Token de meeting do paciente / viewer (dailyMeetingToken). */
  token: string
  /** Duração corrente da chamada (segundos) controlada pelo orquestrador. */
  callDuration: number
  onLeave: () => void
  onConnected?: () => void
  onRemoteDisconnected?: () => void
  onError?: (message: string) => void
}

function formatDuration(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0
  const m = Math.floor(safe / 60)
    .toString()
    .padStart(2, '0')
  const s = (safe % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

export function DailyVideoRoom({
  url,
  token,
  callDuration,
  onLeave,
  onConnected,
  onRemoteDisconnected,
  onError
}: DailyVideoRoomProps) {
  const {
    joined,
    isMuted,
    isCameraOff,
    joinRoom,
    leaveRoom,
    toggleAudio,
    toggleVideo,
    getLocalVideoRef,
    getRemoteVideoRef
  } = useDailyRoom({ onConnected, onRemoteEnded: onRemoteDisconnected, onError })

  useEffect(() => {
    if (url && token) {
      joinRoom(url, token)
    }
    // joinRoom é estável e guardado internamente contra dupla entrada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, token])

  const handleLeave = async () => {
    await leaveRoom()
    onLeave()
  }

  return (
    <div className="relative flex h-full w-full flex-col bg-gray-900">
      {/* Vídeo remoto (médico) - tela principal */}
      <div className="relative flex-1 overflow-hidden bg-gray-800">
        <video ref={getRemoteVideoRef} autoPlay playsInline className="h-full w-full object-cover" />
        {!joined && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-white">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/20 border-t-white" />
              <span className="text-sm">Conectando à consulta...</span>
            </div>
          </div>
        )}
      </div>

      {/* Vídeo local (paciente) - picture-in-picture */}
      <div className="absolute bottom-24 right-4 h-32 w-24 overflow-hidden rounded-lg border-2 border-white/30 shadow-xl md:h-40 md:w-28">
        <video ref={getLocalVideoRef} autoPlay playsInline muted className="h-full w-full -scale-x-100 object-cover" />
        {isCameraOff && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-700">
            <VideoOff className="text-white/70" size={22} />
          </div>
        )}
      </div>

      {/* Barra inferior com timer e controles */}
      <div className="flex flex-col items-center gap-3 bg-gray-900/90 px-4 py-4">
        {joined && <span className="font-mono text-sm text-white/70">{formatDuration(callDuration)}</span>}

        <div className="flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={toggleAudio}
            title={isMuted ? 'Ativar microfone' : 'Desativar microfone'}
            className={`flex h-12 w-12 items-center justify-center rounded-full border-none transition-colors ${
              isMuted ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-white/20 text-white hover:bg-white/30'
            }`}>
            {isMuted ? <MicOff size={22} /> : <Mic size={22} />}
          </button>

          <button
            type="button"
            onClick={toggleVideo}
            title={isCameraOff ? 'Ligar câmera' : 'Desligar câmera'}
            className={`flex h-12 w-12 items-center justify-center rounded-full border-none transition-colors ${
              isCameraOff ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-white/20 text-white hover:bg-white/30'
            }`}>
            {isCameraOff ? <VideoOff size={22} /> : <Video size={22} />}
          </button>

          <button
            type="button"
            onClick={handleLeave}
            title="Encerrar consulta"
            className="flex h-14 w-14 items-center justify-center rounded-full border-none bg-red-600 text-white shadow-lg transition-colors hover:bg-red-700">
            <PhoneOff size={26} />
          </button>
        </div>
      </div>
    </div>
  )
}
